/**
 * ConsentManager
 * Felix Kiesewetter <info@felixkiesewetter.de>
 * v3.8.4
 * 2024-01-23
 */

class ConsentManagerTranslationService
{

    #language = 'de';
    #translations = {
        de: {
            label_accept_all: 'Alle akzeptieren',
            label_deny_all: 'Alle ablehnen',
            label_accept_selected: 'Auswahl akzeptieren',
            label_show_options: 'Einstellungen',
            label_privacy_link: 'Datenschutzerklärung',
            interface_header: 'Wir nehmen Datenschutz ernst',
            interface_text: `Wir tauschen Daten mit Drittanbietern aus, die uns helfen, unser Webangebot zu verbessern. Um bestimmte Dienste verwenden zu dürfen, benötigen wir Ihre Einwilligung. Indem Sie „{label_accept_all}“ klicken, stimmen Sie (jederzeit widerruflich) dieser Datenverarbeitung zu. Unter „{label_show_options}“ können Sie Ihre Einstellungen ändern oder die Datenverarbeitung ablehnen. Weitere Informationen finden Sie in unserer {link_privacy}`,
            property_default: 'Notwendige Cookies',
            property_googleanalytics: 'Google Analytics Tracking',
            property_googlemapsapi: 'Google Maps Kartendienste',
            property_googlemapsembed: 'Google Maps Kartendienste',
            property_googlefonts: 'Google Fonts Schriften',
            property_googletagmanager: 'Google Tag Manager',
            property_youtube: 'YouTube Videos',
            property_sendinblue: 'Sendinblue Tracking',
            property_linkedin: 'LinkedIn Tracking',
            property_facebook: 'Facebook Pixel Tracking',
            notice_page_reload: 'Drücken Sie „OK“, um die Seite neu zu laden und Ihre Einstellungen zu übernehmen.',
            notice_consent_required: 'Inhalt(e) geblockt. Klicken Sie hier, um Ihre Datenschutzeinstellungen anzupassen',
            title_fingerprint: 'Datenschutz-Einstellungen ändern'
        },
        en: {
            label_accept_all: 'Accept all',
            label_deny_all: 'Deny all',
            label_accept_selected: 'Accept selection',
            label_show_options: 'Settings',
            label_privacy_link: 'Privacy policy',
            interface_header: 'We take data protection seriously',
            interface_text: `We exchange data with third-party providers that help us improve our web offering. In order to use certain services, we need your consent. By clicking "{label_accept_all}", you agree (revocable at any time) to this data processing. Under "{label_show_options}" you can change your settings or refuse data processing. For more information, please see our {link_privacy}`,
            property_default: 'Required cookies',
            property_googleanalytics: 'Google Analytics Tracking',
            property_googlemapsapi: 'Google Maps',
            property_googlemapsembed: 'Google Maps',
            property_googlefonts: 'Google Fonts',
            property_googletagmanager: 'Google Tag Manager',
            property_youtube: 'YouTube Videos',
            property_sendinblue: 'Sendinblue Tracking',
            property_linkedin: 'LinkedIn Tracking',
            property_facebook: 'Facebook Pixel Tracking',
            notice_page_reload: 'Press "OK" to reload the page and apply your settings.',
            notice_consent_required: 'Content(s) blocked. Click here to customize your privacy settings',
            title_fingerprint: 'Change privacy settings'
        },
        // TODO: re-implement FR
    };

    constructor(translations = {}, language=null)
    {
        this.setLanguage(language);
        this.#mergeTranslations(translations);
    }

    #mergeTranslations(translations = {})
    {
        if (!Object.values(translations).length) {
            return
        }
        if (Object.values(translations)[0].constructor === Object) {
            Object.keys(translations).forEach(language => {
                if (this.#language !== language) {
                    return
                }
                translations = translations[language]
            })
        }
        Object.keys(translations).forEach((key) => {
            this.set(key, translations[key])
        })
    }

    get(key='')
    {
        return this.#translations[this.#language]?.[key] ?? '';
    }

    getAll()
    {
        return this.#translations[this.#language];
    }

    getLanguage()
    {
        return this.#language;
    }

    setLanguage(language = null)
    {
        this.#language = language || (document.documentElement.getAttribute('lang') || 'en');
        if (2 !== this.#language.length) {
            this.#language = this.#language.substring(0, 2).toLowerCase();
        }
    }

    set(key, value)
    {
        if (!this.#translations[this.#language]) {
            this.#translations[this.#language] = {};
        }
        this.#translations[this.#language][key] = value;
    }

    replaceAll(string)
    {
        if (!this.#translations[this.#language]) {
            return string
        }
        Object.keys(this.#translations[this.#language]).forEach((key) => {
            const value = this.#translations[this.#language][key]
            string = string.replace(`{${key}}`, value)
        });
        return string
    }

}

class ConsentManagerToggleBox
{

    constructor(checkbox)
    {
        const toggleBox = document.createElement('span');
        toggleBox.classList.add('consent-manager__property__toggle');
        if (checkbox.checked) {
            toggleBox.classList.add('-enabled');
        }
        if (checkbox.getAttribute('readonly')) {
            toggleBox.classList.add('-readonly');
        }
        else {
            checkbox.parentNode.addEventListener('click', (e) => {
                if ('A' === e.target.tagName) {
                    return true;
                }
                e.preventDefault();
                toggleBox.classList.toggle('-enabled');
                checkbox.checked = toggleBox.classList.contains('-enabled');
                checkbox.dispatchEvent(new Event('change'));
                toggleBox.parentNode.classList.remove('-highlighted');
            });
        }
        checkbox.style.display = 'none';
        window.addEventListener('consentSettingsUpdated', () => {
            toggleBox.classList.toggle('-enabled', checkbox.checked);
        });
        checkbox.parentNode.insertBefore(toggleBox, checkbox);
    }
}

class ConsentManagerProperty
{
    #debug = false;
    #name = '';
    #enableScriptTag = false;
    #enableStyleTag = false;
    #enableIFrame = false;
    #label = '';
    #uri = '';
    #readonly = false;
    #required = false;
    #allowed = false;
    #consentManager = null;
    #reload = true;
    #cookies = {};
    #callback = null;

    /**
     * ConsentManagerProperty constructor
     *
     * @param {object} options
     * @param {ConsentManager} consentManager
     */
    constructor(options = {}, consentManager)
    {
        this.#consentManager = consentManager;
        this.#mergeOptions(options);
        this.#allowed = this.isAllowed(true);
    }

    /**
     * Initialise the ConsentManagerProperty
     */
    init()
    {
        if (this.#allowed) {
            this.optin();
        }
        else if (this.#cookies) {
            this.#checkCookies();
        }
    }

    #mergeOptions(options)
    {
        this.#debug = options.debug || this.#debug;
        this.#name = options.name || this.#name;
        this.#enableScriptTag = true === options.enableScriptTag ? this.#name : (options.enableScriptTag || false);
        this.#enableStyleTag = true === options.enableStyleTag ? this.#name : (options.enableStyleTag || false);
        this.#enableIFrame = true === options.enableIFrame ? this.#name : (options.enableIFrame || false);
        this.#setLabel(options.label);
        this.#uri = options.uri || this.#uri;
        this.#readonly = options.readonly || options.required ? true : this.#readonly;
        this.#required = options.required || this.#required;
        this.#reload = options.reload || this.#reload;
        this.#cookies = options.cookies || this.#cookies;
    }

    optin()
    {
        if (this.#required) {
            return;
        }
        if (this.#enableScriptTag) {
            this.#consentManager.enableScriptTag(this.#enableScriptTag);
        }
        if (this.#enableStyleTag) {
            this.#consentManager.enableStyleTag(this.#enableStyleTag);
        }
        if (this.#enableIFrame) {
            this.#consentManager.enableIFrame(this.#enableIFrame);
        }
        if (this.#callback) {
            this.#callback(true);
        }
        this.#debug && console.log(`${this.constructor.name}.optin()`, this.#name);
    }

    optout()
    {
        if (this.#reload) {
            this.#consentManager.queuePageReload();
        }
        if (this.#callback) {
            this.#callback(false);
        }
        this.#debug && console.log(`${this.constructor.name}.optout()`, this.#name);
    }

    getLabel()
    {
        // TODO: implement localisation
        return this.#label;
    }

    #setLabel(label)
    {
        if (label) {
            this.#label = label;
            return;
        }
        this.#label = this.#consentManager.getTranslationService().get(`property_${this.#name}`) || this.#label;
    }

    getName()
    {
        return this.#name;
    }

    getUri()
    {
        if (Object === this.#uri.constructor) {
            return this.#uri[this.#consentManager.getTranslationService().getLanguage()] || '/'
        }
        return this.#uri;
    }

    isReadonly()
    {
        return this.#readonly;
    }

    /**
     * Check if a property is allowed and if user interaction is required
     *
     * @param {boolean} onInitialisation
     * @returns {boolean}
     */
    isAllowed(onInitialisation = false)
    {
        if (this.#required) {
            return true
        }
        if (onInitialisation) {
            const consentManagerStorage = this.#consentManager.getStorage()
            if (
                !consentManagerStorage.hasOwnProperty('consent') ||
                !(this.#name in consentManagerStorage.consent)
            ) {
                this.#consentManager.setRequireUserInteraction(true)
                return false
            }
            return consentManagerStorage.consent[this.#name]
        }
        return this.#allowed
    }

    allow()
    {
        if (this.#allowed) {
            return;
        }
        this.optin();
        this.#allowed = true;
    }

    disallow()
    {
        if (!this.#allowed) {
            return;
        }
        this.optout();
        this.#allowed = false;
    }

    setCallback(callback)
    {
        if (!callback || Function !== callback.constructor) {
            return;
        }
        this.#callback = callback;
    }

    // TODO: maybe getCookies(), checkCookies() and clearCookie() should rather be functions of this.#consentManager???

    /**
     * Get document cookies
     *
     * @returns {object}
     */
    #getCookies()
    {
        let cookies = {};
        document.cookie.split(';').forEach((cookie, c) => {
            if (!cookie) {
                return;
            }
            cookies[cookie.split('=')[0].trim()] = cookie.split('=')[1].trim();
        });
        return cookies;
    }

    /**
     * Check if document contains cookies of property
     *
     * @returns {void}
     */
    #checkCookies()
    {
        if (this.#cookies) {
            Object.values(this.#cookies).forEach(propertyCookie => {
                if (propertyCookie.regex || 'undefined' === typeof propertyCookie.regex) {
                    Object.keys(this.#getCookies()).forEach(cookieName => {
                        if (new RegExp(propertyCookie.name, 'g').test(cookieName)) {
                            this.#clearCookie(cookieName, propertyCookie.domain, propertyCookie.path);
                        }
                    });
                }
                // TODO: no other implementation than regex: true so far ...
            });
        }
    }

    /**
     * Clear cookie
     *
     * @param {string} name
     * @param {string} domain
     * @param {string} path
     */
    #clearCookie(name, domain = null, path = null)
    {
        if ('undefined' === typeof this.#getCookies()[name]) {
            return;
        }
        if (!domain) {
            domain = this.#consentManager.getCookieDomain();
        }
        if (!path) {
            path = '/';
        }
        const cookie = `${name}= ;expires= Thu, 01 Jan 1970 00:00:00 GMT; path= ${path}; domain= ${domain}`;
        document.cookie = cookie;
        this.#debug && console.warn(`${this.constructor.name}.#clearCookie()`, name);
    }
}

class ConsentManagerGoogleAnalyticsProperty extends ConsentManagerProperty
{
    id = null;

    constructor(options = {}, consentManager)
    {
        super(options, consentManager);
        this.id = options.id; // TODO: check if set => see ConsentManagerGoogleMapsApiProperty
    }

    optin()
    {
        super.optin();
        const script = document.createElement('script');
        const disableString = `ga-disable-${this.id}`;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${this.id}`;
        script.setAttribute('async', true);
        document.head.insertBefore(script, document.head.firstChild);
        if (typeof window[disableString] !== 'undefined') {
            window[disableString] = false;
        }
        window.dataLayer = window.dataLayer ? window.dataLayer : [];
        function gtag() { dataLayer.push(arguments); }
        gtag('js', new Date());
        gtag('config', this.id, { 'anonymize_ip': true });
    }

    optout()
    {
        super.optout();
        window[`ga-disable-${this.id}`] = true;
    }
}

class ConsentManagerGoogleTagmanagerProperty extends ConsentManagerProperty
{
    id = null;

    constructor(options = {}, consentManager)
    {
        super({...options, ...{ reload: true }}, consentManager);
        this.id = options.id; // TODO: check if set => see ConsentManagerGoogleMapsApiProperty
    }

    optin()
    {
        super.optin();
        // TODO: beautify this? (see LinkedIn)
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer', this.id);
        const noScript = document.createElement('noScript');
        const noScriptIFrame = document.createElement('iframe');
        noScriptIFrame.src = `https://www.googletagmanager.com/ns.html?id=${this.id}`;
        noScriptIFrame.style.height = 0;
        noScriptIFrame.style.width = 0;
        noScriptIFrame.style.display = 'none';
        noScriptIFrame.style.visibility = 'none';
        noScript.appendChild(noScriptIFrame);
        document.body.insertBefore(noScript, document.body.firstChild);
    }
}

class ConsentManagerGoogleMapsApiProperty extends ConsentManagerProperty
{
    constructor(options = {}, consentManager)
    {
        super({...options, ...{ enableScriptTag: true }}, consentManager);
        if (options.key) {
            options.id = options.key;
        }
        if (!options.id) {
            return consentManager.error('Google Maps API requires option "id" (or "key") which is the API key.');
        }
        this.id = options.id;
    }

    /**
     * Opt-in Google Maps API
     *
     * Create external script with Google Maps API key
     * When that script has been loaded => enable all script tags with data-consent="googlemapsapi"
     */
    optin()
    {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?v=3.exp&key=${this.id}`;
        script.onload = () => {
            super.optin();
        };
        document.body.appendChild(script);
    }
}

class ConsentManagerGoogleMapsEmbedProperty extends ConsentManagerProperty
{
    constructor(options = {}, consentManager)
    {
        super({...options, ...{ enableIFrame: true }}, consentManager);
    }
}

class ConsentManagerYouTubeProperty extends ConsentManagerProperty
{
    constructor(options = {}, consentManager)
    {
        super({...options, ...{ enableIFrame: true }}, consentManager);
    }
}

class ConsentManagerLinkedInInsightTagProperty extends ConsentManagerProperty
{
    id = null;

    constructor(options = {}, consentManager)
    {
        super({...options, ...{ reload: true }}, consentManager);
        this.id = options.id; // TODO: check if set => see ConsentManagerGoogleMapsApiProperty
    }

    optin()
    {
        super.optin();
        window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
        window._linkedin_data_partner_ids.push(this.id);
        if (!window.lintrk) {
            window.lintrk = function (a,b) {
                window.lintrk.q.push([a,b]);
            };
            window.lintrk.q=[];
        }
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        document.querySelector('script').parentNode.insertBefore(script, document.querySelector('script'));
        const noScript = document.createElement('noscript');
        const noScriptImg = document.createElement('img');
        noScriptImg.width = 1;
        noScriptImg.height = 1;
        noScriptImg.style.display = 'none';
        noScriptImg.alt = 'LinkedIn Insight-Tag';
        noScriptImg.src = `https://px.ads.linkedin.com/collect/?pid=${this.id}&fmt=gif`;
        noScript.appendChild(noScriptImg);
        document.body.insertBefore(noScript, document.body.firstChild);
    }
}

class ConsentManager
{

    #settings = {
        debug: false,
        disableFrames: true,
        interfaceTheme: ConsentManager.THEME_LIGHT,
        interfaceLayout: ConsentManager.LAYOUT_BOTTOM_LEFT,
        interfaceMode: ConsentManager.MODE_ACCEPT_SELECTED,
        interfaceHtml: '<h6>{interface_header}</h6><p>{interface_text}</p>',
        queuePageReloadDelay: 1000,
        toggleBoxes: true,
        fingerprintIcon: true,
        fingerprintIconLayout: ConsentManager.FINGERPRINT_LAYOUT_BOTTOM_RIGHT,
        privacyUri: '',
        patterns: {
            youtube: 'youtu',
            googlemaps: 'googlemaps'
        },
    };
    #translationService;
    #properties = {};
    #interface;
    #interfaceFieldset;
    #interfaceToggle;
    #interfaceButtons = {};
    #requireUserInteraction = false;
    #queuePageReloadTimeout = null;
    #storageIdentifier = 'ConsentManagerStorage';
    #supportedProperties = {
        googleanalytics: ConsentManagerGoogleAnalyticsProperty,
        googletagmanager: ConsentManagerGoogleTagmanagerProperty,
        googlemapsapi: ConsentManagerGoogleMapsApiProperty,
        googlemapsembed: ConsentManagerGoogleMapsEmbedProperty,
        youtube: ConsentManagerYouTubeProperty,
        linkedininsighttag: ConsentManagerLinkedInInsightTagProperty,
        // TODO googlefonts: ConsentManagerGoogleFontsProperty,
        // TODO facebookpixel: ConsentManagerFacebookPixelProperty,
        // TODO adobefonts: ConsentManagerAdobeFontsProperty
    };
    #errors = {};
    #isConstructed = false;

    /**
     * ConsentManager constructor
     *
     * @param {object} options
     * @param {object} properties
     * @param {object} translations
     */
    constructor(options, properties, translations)
    {
        if (!options.privacyUri) {
            options.debug && console.error('ConsentManager.#constructor()', 'The option privacyUri is mandatory');
            return;
        }

        this.#mergeSettings(options);
        this.#loadTranslationService(translations);
        this.#buildInterface();
        this.#addInitialProperties(properties);
        this.#disableFrames();
        // TODO: this.bindFrames();

        this.#addEventHandlers();
        if (this.#requireUserInteraction) {
            this.showInterface();
        }
        this.#isConstructed = true;
    }

    /**
     * Merge options with default settings
     *
     * @param {Object} options
     */
    #mergeSettings(options)
    {
        Object.keys(options).forEach(option => {
            if ('undefined' === typeof this.#settings[option]) {
                (options.debug || this.#settings.debug) && console.warn('ConsentManager.#mergeSettings()', `Option "${option}" does not exist and will be ignored`)
                delete options[option]
            }
        })
        this.#settings = {...this.#settings, ...options}
        this.#settings.debug && console.log('ConsentManager.#settings', this.#settings)
    }

    #loadTranslationService(translations)
    {
        this.#translationService = new ConsentManagerTranslationService(translations);
    }

    #buildInterface()
    {
        this.#interface = document.getElementById('consent-manager');
        if (this.#interface) {
            return;
        }

        this.#interface = document.createElement('aside');
        this.#interface.id = 'consent-manager';
        this.#interface.classList.add('consent-manager');
        this.#interface.classList.add(`consent-manager--${this.#settings.interfaceLayout}`);
        this.#interface.classList.add(`consent-manager--${this.#settings.interfaceTheme}`);
        let form = document.createElement('form');
        const content = document.createElement('div');
        content.classList.add('consent-manager__content');
        content.innerHTML = this.#settings.interfaceHtml;
        form.appendChild(content);
        for (const key in this.#translationService.getAll()) {
            const value = this.#translationService.get(key);
            form.innerHTML = form.innerHTML.replace(`{${key}}`, value);
        }
        form.innerHTML = form.innerHTML.replace('{link_privacy}', `<a href="${this.getPrivacyUri()}" target="_blank">${this.#translationService.get('label_privacy_link')}</a>`);
        form.innerHTML = this.#translationService.replaceAll(form.innerHTML);
        form.id = 'consent-manager__form';
        form.classList.add('consent-manager__form');
        const interfaceDismiss = document.createElement('button');
        interfaceDismiss.textContent = '×';
        interfaceDismiss.classList.add('consent-manager__dismiss');
        interfaceDismiss.addEventListener('click', (e) => {
            e.preventDefault();
            this.hideInterface();
        });
        this.#interface.appendChild(interfaceDismiss);
        this.#interfaceFieldset = document.createElement('fieldset');
        this.#interfaceFieldset.classList.add('consent-manager__fieldset');
        if (ConsentManager.MODE_ACCEPT_SELECTED !== this.#settings.interfaceMode) {
            this.#interfaceFieldset.style.display = 'none';
        }
        form.appendChild(this.#interfaceFieldset);
        const buttonWrapper = document.createElement('div');
        buttonWrapper.classList.add('consent-manager__buttons');
        form.appendChild(buttonWrapper);
        if (ConsentManager.MODE_ACCEPT_SELECTED !== this.#settings.interfaceMode) {
            this.#interfaceButtons['acceptAll'] = document.createElement('button');
            this.#interfaceButtons['acceptAll'].textContent = this.#translationService.get('label_accept_all');
            this.#interfaceButtons['acceptAll'].id = 'consent-manager__accept-all';
            this.#interfaceButtons['acceptAll'].classList.add('consent-manager__button');
            this.#interfaceButtons['acceptAll'].classList.add('consent-manager__button--accept-all');
            buttonWrapper.appendChild(this.#interfaceButtons['acceptAll']);
        }
        this.#interfaceButtons['acceptSelected'] = document.createElement('button');
        this.#interfaceButtons['acceptSelected'].textContent = this.#translationService.get('label_accept_selected');
        this.#interfaceButtons['acceptSelected'].id = 'consent-manager__accept-selected';
        this.#interfaceButtons['acceptSelected'].classList.add('consent-manager__button');
        this.#interfaceButtons['acceptSelected'].classList.add('consent-manager__button--accept-selected');
        buttonWrapper.appendChild(this.#interfaceButtons['acceptSelected']);
        if (ConsentManager.MODE_ACCEPT_SELECTED !== this.#settings.interfaceMode) {
            if (ConsentManager.MODE_ACCEPT_ALL_DENY_ALL === this.#settings.interfaceMode) {
                this.#interfaceButtons['denyAll'] = document.createElement('button');
                this.#interfaceButtons['denyAll'].textContent = this.#translationService.get('label_deny_all');
                this.#interfaceButtons['denyAll'].id = 'consent-manager__deny-all';
                this.#interfaceButtons['denyAll'].classList.add('consent-manager__button');
                this.#interfaceButtons['denyAll'].classList.add('consent-manager__button--deny-all');
                buttonWrapper.appendChild(this.#interfaceButtons['denyAll']);
            }
        }
        if (ConsentManager.MODE_ACCEPT_SELECTED !== this.#settings.interfaceMode) {
            this.#interfaceButtons['acceptSelected'].style.display = 'none';
            this.#interfaceButtons['showOptions'] = document.createElement('button');
            this.#interfaceButtons['showOptions'].textContent = this.#translationService.get('label_show_options');
            this.#interfaceButtons['showOptions'].id = 'consent-manager__show-options';
            this.#interfaceButtons['showOptions'].classList.add('consent-manager__button');
            this.#interfaceButtons['showOptions'].classList.add('consent-manager__button--show-options');
            buttonWrapper.appendChild(this.#interfaceButtons['showOptions']);
        }
        this.#interface.appendChild(form);
        document.body.appendChild(this.#interface);
        if (!this.#settings.fingerprintIcon) {
            return;
        }
        let interfaceToggle = document.getElementById('consent-manager-toggle');
        if (!interfaceToggle) {
            interfaceToggle = document.createElement('a');
            interfaceToggle.id = 'consent-manager__fingerprint';
            interfaceToggle.classList.add('consent-manager__fingerprint');
            interfaceToggle.classList.add(`consent-manager__fingerprint--${this.#settings.fingerprintIconLayout}`);
            interfaceToggle.rel = 'consent-manager';
            interfaceToggle.title = this.#translationService.get('title_fingerprint');
            interfaceToggle.innerHTML =
                '<svg height="48" viewBox="0 0 48 48" width="48" xmlns="http://www.w3.org/2000/svg">\n' +
                '    <path d="M35.62 8.94c-.16 0-.31-.04-.46-.11C31.33 6.85 28 6 24.02 6c-3.97 0-7.71.95-11.14 2.82-.49.26-1.09.09-1.36-.4-.26-.49-.09-1.09.4-1.36C15.65 5.03 19.72 4 24.02 4c4.26 0 7.98.94 12.06 3.05.49.25.68.86.43 1.35-.18.34-.53.54-.89.54zM7 19.44c-.2 0-.4-.06-.58-.18-.45-.32-.56-.94-.24-1.39 1.98-2.8 4.51-5 7.51-6.55 6.29-3.25 14.33-3.26 20.63-.02 2.99 1.54 5.51 3.72 7.5 6.5.32.45.22 1.07-.23 1.39-.45.32-1.08.22-1.4-.23-1.8-2.52-4.08-4.5-6.78-5.88-5.74-2.95-13.07-2.94-18.8.02-2.71 1.4-5 3.39-6.79 5.93-.2.27-.51.41-.82.41zm12.51 24.13c-.26 0-.51-.1-.71-.3-1.73-1.75-2.67-2.86-4.02-5.27-1.38-2.46-2.11-5.47-2.11-8.69 0-5.94 5.08-10.78 11.33-10.78s11.33 4.83 11.33 10.78c0 .55-.45 1-1 1s-1-.45-1-1c0-4.84-4.18-8.78-9.33-8.78-5.14 0-9.33 3.94-9.33 8.78 0 2.88.64 5.54 1.85 7.71 1.29 2.3 2.15 3.29 3.69 4.84.39.39.39 1.03-.01 1.41-.18.21-.44.3-.69.3zm14.33-3.7c-2.38 0-4.47-.6-6.2-1.77-2.97-2.02-4.75-5.3-4.75-8.78 0-.55.45-1 1-1s1 .45 1 1c0 2.81 1.45 5.47 3.88 7.12 1.41.96 3.07 1.43 5.07 1.43.48 0 1.29-.05 2.09-.19.54-.1 1.06.27 1.16.81.1.54-.27 1.06-.81 1.16-1.17.21-2.16.22-2.44.22zM29.81 44c-.09 0-.18-.01-.26-.04-3.19-.87-5.27-2.05-7.43-4.2-2.79-2.78-4.33-6.49-4.33-10.44 0-3.25 2.76-5.89 6.16-5.89 3.4 0 6.16 2.64 6.16 5.89 0 2.14 1.87 3.89 4.16 3.89s4.16-1.74 4.16-3.89c0-7.54-6.5-13.67-14.49-13.67-5.69 0-10.88 3.16-13.22 8.06-.78 1.62-1.17 3.51-1.17 5.61 0 1.56.14 4.02 1.33 7.21.19.52-.07 1.09-.59 1.29-.52.19-1.09-.07-1.29-.59-.98-2.63-1.46-5.21-1.46-7.91 0-2.4.46-4.58 1.37-6.47 2.67-5.58 8.57-9.19 15.02-9.19 9.09 0 16.49 7.03 16.49 15.67 0 3.25-2.77 5.89-6.16 5.89s-6.16-2.64-6.16-5.89c0-2.14-1.87-3.89-4.16-3.89s-4.16 1.74-4.16 3.89c0 3.41 1.33 6.62 3.74 9.02 1.89 1.88 3.73 2.92 6.55 3.69.53.15.85.7.7 1.23-.12.44-.52.73-.96.73z"/>\n' +
                '</svg>';
            document.body.appendChild(interfaceToggle);
        }

        // Escape key will dismiss the interface if no interaction is required
        document.addEventListener('keyup', (e) => {
            if ('Escape' === e.key && this.#interface.classList.contains('-visible')) {
                if (!this.#requireUserInteraction) {
                    this.hideInterface();
                    return;
                }
                console.warn('ConsentManager interface cannot be dismissed – user interaction is required!');
            }
        });
        this.#interfaceToggle = interfaceToggle;
    }

    #updateInterface()
    {
        this.#interfaceFieldset.innerHTML = '';
        Object.keys(this.#properties).forEach((propertyName) => {
            const property = this.#properties[propertyName];
            let label = document.createElement('label');
            let checkbox = document.createElement('input');
            label.setAttribute('for', `consent-manager__property--${propertyName}`);
            label.classList.add('consent-manager__property');
            label.classList.add(`consent-manager__property--${propertyName}`);
            checkbox.setAttribute('type', 'checkbox');
            checkbox.id = `consent-manager__property--${propertyName}`;
            checkbox.classList.add('consent-manager__property__checkbox');
            checkbox.name = `allow[${propertyName}]`;
            if (property.isReadonly()) {
                checkbox.setAttribute('readonly', true);
                checkbox.setAttribute('onclick', 'return false;');
            }
            if (property.isAllowed()) {
                checkbox.setAttribute('checked', true);
            }
            label.appendChild(checkbox);
            let labelText = property.getLabel();
            if (property.getUri()) {
                labelText = `<a href="${property.getUri()}" class="consent-manager__property__link" target="_blank">${labelText}</a>`
            }
            label.innerHTML += ` ${labelText}`;
            this.#interfaceFieldset.appendChild(label);
        });
        if (this.#settings.toggleBoxes) {
            for (const checkbox of this.#interface.querySelectorAll('input[type="checkbox"]')) {
                new ConsentManagerToggleBox(checkbox);
                if (ConsentManager.MODE_ACCEPT_ALL_DENY_ALL === this.#settings.interfaceMode) {
                    checkbox.addEventListener('change', () => {
                        this.#interfaceButtons['acceptSelected'].classList.add('-activated');
                    });
                }
            }
        }
    }

    #addInitialProperties(properties = {})
    {
        this.addProperty({
            name: 'default',
            label: this.#translationService.get('property_default'),
            readonly: true,
            required: true
        }, true);
        if (Object.values(properties).length) {
            Object.values(properties).forEach((propertyOptions) => {
                this.addProperty(propertyOptions, true);
            });
        }
        this.#settings.debug && console.log('ConsentManager.#properties', this.#properties);
    }

    /**
     * Disable frames
     */
    #disableFrames()
    {
        if (true !== this.#settings.disableFrames) {
            return;
        }
        const frames = document.querySelectorAll('iframe[src*="/googlemaps/"], iframe[src*="youtu"]');
        if (!Object.values(frames).length) {
            return;
        }
        for (const frame of frames) {
            const src = frame.src;
            Object.keys(this.#settings.patterns).forEach((property) => {
                if (new RegExp(this.#settings.patterns[property]).test(src)) {
                    if (false === this.getConsent(property) && !frame.dataset.src) {
                        frame.setAttribute('data-src', src);
                        frame.removeAttribute('src');
                        frame.setAttribute('data-consent', property);
                    }
                }
            });
        }
    }

    /**
     * Add event handlers to buttons and components
     *
     * @returns {void}
     */
    #addEventHandlers()
    {
        if (this.#interfaceButtons['showOptions'] && this.#interfaceFieldset) {
            this.#interfaceButtons['showOptions'].addEventListener('click', () => {
                this.#interfaceFieldset.classList.add('-visible')
                this.#settings.interfaceMode = 'accept-selected'
                this.#interfaceButtons['showOptions'].remove()
                this.#interfaceButtons['acceptSelected']?.style.setProperty('display', 'flex', 'important')
            })
        }
        if (this.#interfaceButtons['acceptAll']) {
            this.#interfaceButtons['acceptAll'].addEventListener('click', (e) => {
                e.stopPropagation()
                e.preventDefault()
                this.#updateProperties(true)
            })
        }
        if (this.#interfaceButtons['denyAll']) {
            this.#interfaceButtons['denyAll'].addEventListener('click', (e) => {
                e.stopPropagation()
                e.preventDefault()
                this.#updateProperties(false, true)
            })
        }
        if (this.#interfaceButtons['acceptSelected']) {
            this.#interfaceButtons['acceptSelected'].addEventListener('click', (e) => {
                e.stopPropagation()
                e.preventDefault()
                this.#updateProperties(false)
            })
        }
        for (const trigger of document.querySelectorAll('[rel="consent-manager"]')) {
            trigger.addEventListener('click', (e) => {
                e.preventDefault()
                this.showInterface(true, trigger.dataset.consent)
            })
        }
        window.addEventListener('click', (e) => {
            if ('consent-manager' !== e.target.rel) {
                return true
            }
            if (null !== e.target.parentNode && 'consent-manager' !== e.target.parentNode.rel) {
                return true
            }
            e.preventDefault()
            this.showInterface(true)
        })
    }

    /**
     * Update properties
     *
     * @param acceptAll
     * @param denyAll
     */
    #updateProperties(acceptAll = false, denyAll = false)
    {
        const checkboxes = this.#interfaceFieldset.querySelectorAll('input[type="checkbox"][name]:not([readonly])');
        let storeConsent = {};
        if (true === acceptAll) {
            for (const checkbox of checkboxes) {
                checkbox.checked = true;
            }
        }
        else if (true === denyAll) {
            for (const checkbox of checkboxes) {
                checkbox.checked = false;
            }
        }
        for (const checkbox of checkboxes) {
            const propertyName = checkbox.name.split('[')[1].split(']')[0];
            const property = this.#properties[propertyName];
            storeConsent[propertyName] = false;
            if (checkbox.checked) {
                storeConsent[propertyName] = true;
                if (!property.isAllowed()) {
                    property.allow();
                }
            }
            else if (!checkbox.checked && property.isAllowed()) {
                property.disallow();
            }
        }
        try {
            const storeData = {
                consent: storeConsent,
                timestamp: Date.now().toString()
            };
            localStorage.setItem(this.#storageIdentifier, JSON.stringify(storeData));
            this.#settings.debug && console.log('ConsentManager.#updatePropertes()', storeData);
        }
        catch (error) {
            this.#errors.push(error);
        }
        this.hideInterface();
        this.#requireUserInteraction = false;
        window.dispatchEvent(new CustomEvent('consentSettingsUpdated'));
    }

    addProperty(options = {}, onInitialisation = false)
    {
        if (!options.name) {
            console.warn('[ConsentManager]', 'Adding a property requires a name');
            return;
        }
        options.debug = this.#settings.debug;
        if (options.name in this.#supportedProperties) {
            this.#properties[options.name] = new this.#supportedProperties[options.name](options, this)
        }
        else {
            this.#properties[options.name] = new ConsentManagerProperty(options, this);
        }
        this.#properties[options.name].init();
        this.#settings.debug && !onInitialisation && console.log('ConsentManager.addProperty()', options);
        this.#updateInterface();
    }

    /**
     * Await window.ConsentManager
     *
     * @param callback
     */
    static await(callback)
    {
        if (!callback || Function !== callback.constructor) {
            return
        }
        const waitForConsentManager = setInterval(() => {
            // TODO: What if the globally set ConsentManager is NOT stored in window.ConsentManager??
            // TODO: Either force window.ConsentManager on initialisation (constructor()) or make it dynamic via func arg
            if (window.ConsentManager) {
                clearInterval(waitForConsentManager)
                callback(window.ConsentManager)
            }
        }, 250)
    }

    /**
     * Register callback function for a property
     *
     * The callback function will also be called right away
     *
     * @param {string} property
     * @param {function|null} callback
     */
    registerPropertyCallback(property, callback = null)
    {
        if (!property || 0 > Object.keys(this.#properties).indexOf(property)) {
            console.warn('ConsentManager.registerPropertyCallback()', `Property "${property}" does not exist.`)
            return
        }
        if (!callback || Function !== callback.constructor) {
            console.warn('ConsentManager.registerPropertyCallback()', `Callback "${callback}" is not a function.`)
            return
        }
        this.#properties[property].setCallback(callback)
        this.#settings.debug && console.log('ConsentManager.registerPropertyCallback()', property, callback);
        callback(this.getConsent(property))
    }

    /**
     * Show interface
     *
     * @param {boolean} expandAllOptions
     * @param {string|null} highlightOption
     */
    showInterface(expandAllOptions = false, highlightOption = null)
    {
        this.#interfaceButtons['acceptSelected']?.style.setProperty('display',  (this.usesOnlyDefaultProperty() ? 'none' : (expandAllOptions ? 'flex' : 'none')), 'important')
        this.#interfaceButtons['denyAll']?.style.setProperty('display',  (this.usesOnlyDefaultProperty() ? 'none' : 'flex'), 'important')
        if (this.#interfaceButtons['showOptions'] && expandAllOptions) {
            this.#interfaceButtons['showOptions'].dispatchEvent(new Event('click'))
        }
        if (highlightOption) {
            const highlightedOption = this.#interfaceFieldset.querySelector(`[for="consent-manager__property--${highlightOption}"]`)
            if (highlightedOption) {
                highlightedOption.classList.add('-highlighted')
                setTimeout(() => {
                    highlightedOption.classList.remove('-highlighted')
                }, 5000)
            }
        }
        this.#interface.classList.add('-visible')
    }

    /**
     * Hide interface
     */
    hideInterface()
    {
        this.#interface.classList.remove('-visible');
    }

    /**
     * Check if the current instance only uses the default property (required cookies)
     *
     * @returns {boolean}
     */
    usesOnlyDefaultProperty()
    {
        return 1 === Object.keys(this.#properties).length && 'default' === Object.keys(this.#properties)[0]
    }

    enableScriptTag(propertyName)
    {
        const scriptTags = document.querySelectorAll(`script[type="text/plain"][data-consent="${propertyName}"]`);
        if (scriptTags.length) {
            scriptTags.forEach((scriptTag) => {
                scriptTag.parentNode.removeChild(scriptTag);
                scriptTag.setAttribute('type', 'text/javascript');
                if (scriptTag.hasAttribute('data-src')) {
                    scriptTag.setAttribute('src', scriptTag.getAttribute('data-src'));
                    scriptTag.removeAttribute('data-src');
                }
                document.body.appendChild(scriptTag);
                this.#settings.debug && console.log(`ConsentManager.#enableScriptTag(${propertyName})`, `Enabled \<script\> with attribute data-consent="${propertyName}"`);
            });
        }
        else {
            this.#settings.debug && console.warn(`ConsentManager.#enableScriptTag(${propertyName})`, `There is no \<script\> with attribute data-consent="${propertyName}"`);
        }
    }

    enableStyleTag(propertyName)
    {
        const styleTags = document.querySelectorAll(`style[type="text/plain"][data-consent="${propertyName}"], link[type="text/plain"][data-consent="${propertyName}"]`);
        if (styleTags) {
            styleTags.forEach((styleTag) => {
                styleTag.parentNode.removeChild(styleTag);
                styleTag.setAttribute('type', 'text/css');
                if (styleTag.hasAttribute('data-src')) {
                    styleTag.setAttribute('src', styleTag.getAttribute('data-src'));
                    styleTag.removeAttribute('data-src');
                }
                if (styleTag.hasAttribute('data-href')) {
                    styleTag.setAttribute('href', styleTag.getAttribute('data-href'));
                    styleTag.removeAttribute('data-href');
                }
                document.head.appendChild(styleTag);
                const styleTagName = styleTag.tagName.toLowerCase();
                this.#settings.debug && console.log(`ConsentManager.#enablestyleTag(${propertyName})`, `Enabled \<${styleTagName}\> with attribute data-consent="${propertyName}"`);
            });
        }
        else {
            this.#settings.debug && console.warn(`ConsentManager.#enablestyleTag(${propertyName})`, `There is no style tag with attribute data-consent="${propertyName}"`);
        }
    }

    enableIFrame(propertyName)
    {
        const iFrames = document.querySelectorAll(`iframe[data-src][data-consent="${propertyName}"]`);
        if (iFrames) {
            iFrames.forEach((iFrame) => {
                iFrame.src = iFrame.dataset.src;
                iFrame.removeAttribute('data-src');
            });
        }
        else {
            this.#settings.debug && console.warn(`ConsentManager.#enableIFrame(${propertyName})`, `There is no iframe with attribute data-consent="${propertyName}"`);
        }
    }

    queuePageReload()
    {
        if (null === this.#queuePageReloadTimeout) {
            this.#settings.debug && console.warn(`ConsentManager.queuePageReload()`);
            let c = confirm(this.#translationService.get('notice_page_reload'));
            if (c) {
                this.#queuePageReloadTimeout = setTimeout(() => {
                    window.location.reload();
                }, this.#settings.queuePageReloadDelay);
            }
        }
    }

    /**
     * Throw an error
     *
     * @param errorMessage
     */
    error(errorMessage = 'Oops, an error occured')
    {
        console.error('ConsentManager.error', errorMessage);
        return;
    }

    getConsent(propertyName)
    {
        if (!(propertyName in this.#properties)) {
            console.warn('ConsentManager.#getConsent', `Property "${propertyName}" does not exist`);
            return false;
        }
        return this.#properties[propertyName].isAllowed();
    }

    /**
     * Get privacy uri
     *
     * @returns {String}
     */
    getPrivacyUri()
    {
        if (this.#settings.privacyUri.constructor === Object) {
            return this.#settings.privacyUri[this.#translationService.getLanguage()] || '/'
        }
        return this.#settings.privacyUri
    }

    /**
     * Get (local) storage and check if saved settings are still valid
     *
     * @returns {Object}
     */
    getStorage()
    {
        const storage = JSON.parse(localStorage.getItem(this.#storageIdentifier)) || {}
        if (
            Object !== storage.constructor ||
            !storage.hasOwnProperty('consent') ||
            !storage.hasOwnProperty('timestamp') ||
            Date.now() > (parseInt(storage.timestamp)) + (24 * 3600 * 1000) // TODO: make configurable
        ) {
            this.setRequireUserInteraction(true)
            return {}
        }
        return storage
    }

    getTranslationService()
    {
        return this.#translationService;
    }

    /**
     * Get cookie domain without subdomain with leading .
     *
     * @returns {string}
     */
    getCookieDomain()
    {
        return `.${window.location.hostname.replace(/^[^.]+\./g, '')}`;
    }

    setConsent(propertyName, allow = false, reload = false)
    {
        if (!(propertyName in this.#properties)) {
            console.warn('ConsentManager.#setConsent', `Property "${propertyName}" does not exist`);
            return;
        }
        this.#settings.debug && console.log('ConsentManager.#setConsent', 'setConsent()', arguments);
        if (allow) {
            this.#properties[propertyName].allow();
        }
        else {
            this.#properties[propertyName].disallow();
        }
        // TODO: Requires an update of local storage
        // TODO: Also, does not seem to work ...
        window.dispatchEvent(new CustomEvent('consentSettingsUpdated'));
        if (reload) {
            this.queuePageReload();
        }
    }

    setRequireUserInteraction(requireUserInteraction)
    {
        this.#requireUserInteraction = requireUserInteraction;
        if (this.#isConstructed && requireUserInteraction) {
            this.showInterface();
        }
    }









    // TODO: re-implement (not used since v3.0.0)
    /**
     * Bind frames to the consent manager
     *
     * @param {node} frame
     * @returns {void}
     */
    bindFrames()
    {
        const frames = document.querySelectorAll('iframe[data-src]');
        const getProperty = (src) => {
            let property = null
            Object.keys(this.#settings.patterns).forEach((p) => {
                if (new RegExp(this.#settings.patterns[p]).test(src)) {
                    property = p;
                }
            });
            return property;
        };
        for (const frame of frames) {
            const property = frame.getAttribute('data-consent') || getProperty(frame.getAttribute('data-src'));
            if (null === property) {
                return;
            }
            else if (this.getConsent(property)) {
                this.enableFrame(frame);
            }
            else {
                const parent = frame.parentNode;
                const wrapper = document.createElement('aside');
                const text = document.createElement('p');
                text.classList.add('consent-required-text');
                text.innerHTML = this.translationService.get('requireConsent');
                text.addEventListener('click', () => {
                    this.showInterface(true);
                });
                window.addEventListener('consentSettingsUpdated', () => {
                    if (this.getConsent(property)) {
                        this.enableFrame(frame, parent, wrapper);
                    }
                });
                parent.insertBefore(wrapper, frame);
                wrapper.classList.add('consent-required');
                wrapper.appendChild(frame);
                wrapper.appendChild(text);
            }
        }
    }

    static THEME_LIGHT = 'theme-light'
    static THEME_DARK = 'theme-dark'
    static LAYOUT_CENTER = 'center'
    static LAYOUT_BOTTOM_LEFT = 'bottom-left'
    static LAYOUT_BOTTOM_WIDE = 'bottom-wide'
    static LAYOUT_BOTTOM_RIGHT = 'bottom-right'
    static FINGERPRINT_LAYOUT_BOTTOM_LEFT = 'bottom-left'
    static FINGERPRINT_LAYOUT_BOTTOM_RIGHT = 'bottom-right'
    static LAYOUT_TOP_LEFT = 'top-left'
    static LAYOUT_TOP_WIDE = 'top-wide'
    static LAYOUT_TOP_RIGHT = 'top-right'
    static MODE_ACCEPT_ALL = 'accept-all'
    static MODE_ACCEPT_ALL_DENY_ALL = 'accept-all-deny-all'
    static MODE_ACCEPT_SELECTED = 'accept-selected'

}

export default ConsentManager